import { registerObservation } from "../slices/observationSlice";
import { logErrorEvent } from "../services/logService";

/**
 * Fetch an Observation by ID
 * - Checks Redux store first
 * - Fetches from FHIR server if missing
 * - Registers into Redux
 */
export const fetchObservation = async (observationId, fhirClient, store) => {
    //console.log("fetching observation by id");
    if (!observationId || !fhirClient) return null;

    // Check Redux cache
    const cached = store.getState().observations?.[observationId];
    if (cached) {
        //console.log(`🧠 Observation ${observationId} served from cache`);
        return cached;
    }

    try {
        //console.log(`🌐 Fetching Observation/${observationId} from FHIR`);
        const fetched = await fhirClient.request(`Observation/${observationId}`);

        if (fetched?.resourceType !== "Observation") {
            console.warn("⚠️ Unexpected observation response:", fetched);
            return null;
        }

        store.dispatch(registerObservation(fetched));
        return fetched;

    } catch (err) {
        console.error("❌ Failed to fetch observation:", err);
        const status = err?.response?.status || err?.status || 500;
        logErrorEvent({
            errorType: "read",
            error: {
                message: err?.message || "Observation fetch failed",
                status,
                resourceId: `Observation/${observationId}`
            },
            description: `Failed to retrieve Observation/${observationId} from FHIR server`,
            store
        });
        return { error: status };
    }
};

/**
 * Fetch an Observation given a FHIR reference string
 * e.g. "Observation/123" or "Observation/123/_history/4"
 */
export const fetchObservationByReference = async (ref, fhirClient, store) => {

    if (!ref || typeof ref !== "string") {
        console.warn("⚠️ Invalid observation reference (not a string):", ref);
        return null;
    }

    if (!ref.startsWith("Observation/")) {
        console.warn("⚠️ Reference is not an Observation:", ref);
        return null;
    }

    // Extract ID safely (handles versioned refs)
    // "Observation/123" → "123"
    // "Observation/123/_history/5" → "123"
    const parts = ref.split("/");
    const observationId = parts[1];

    if (!observationId || observationId === "undefined" || observationId === "null") {
        console.warn("⚠️ Could not extract valid observation ID from reference:", ref);
        return null;
    }

    return fetchObservation(observationId, fhirClient, store);
};
